import Vue from 'vue'
import Meta from 'vue-meta'
import { createRouter } from './router.js'
import NoSsr from './components/no-ssr.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from './components/nuxt-error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_googleanalytics_68dd8e7e from 'nuxt_plugin_googleanalytics_68dd8e7e' // Source: ./google-analytics.js (mode: 'client')
import nuxt_plugin_nuxtdevicedetectplugin4789340e_298842d9 from 'nuxt_plugin_nuxtdevicedetectplugin4789340e_298842d9' // Source: ./nuxt-device-detect.plugin.4789340e.js (mode: 'all')
import nuxt_plugin_cookieuniversalnuxt_4d00bf60 from 'nuxt_plugin_cookieuniversalnuxt_4d00bf60' // Source: ./cookie-universal-nuxt.js (mode: 'all')
import nuxt_plugin_axios_37ac50a8 from 'nuxt_plugin_axios_37ac50a8' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_axios_3566aa80 from 'nuxt_plugin_axios_3566aa80' // Source: ../../plugins/axios (mode: 'all')
import nuxt_plugin_vueIndicative_328cb63c from 'nuxt_plugin_vueIndicative_328cb63c' // Source: ../../plugins/vueIndicative.js (mode: 'client')
import nuxt_plugin_vueBugsnag_10b846e6 from 'nuxt_plugin_vueBugsnag_10b846e6' // Source: ../../plugins/vueBugsnag (mode: 'all')
import nuxt_plugin_vueAwesome_41c0d9d8 from 'nuxt_plugin_vueAwesome_41c0d9d8' // Source: ../../plugins/vueAwesome (mode: 'all')
import nuxt_plugin_vuetify_0e1f10d7 from 'nuxt_plugin_vuetify_0e1f10d7' // Source: ../../plugins/vuetify (mode: 'all')
import nuxt_plugin_appyUtil_3ace1371 from 'nuxt_plugin_appyUtil_3ace1371' // Source: ../../plugins/appyUtil (mode: 'all')
import nuxt_plugin_vueMoment_7f2e95a2 from 'nuxt_plugin_vueMoment_7f2e95a2' // Source: ../../plugins/vueMoment (mode: 'all')
import nuxt_plugin_vueScrollto_09b8cdc0 from 'nuxt_plugin_vueScrollto_09b8cdc0' // Source: ../../plugins/vueScrollto.js (mode: 'client')
import nuxt_plugin_vueClipboard_d0a87ee0 from 'nuxt_plugin_vueClipboard_d0a87ee0' // Source: ../../plugins/vueClipboard.js (mode: 'client')
import nuxt_plugin_vueSwiper_5f742e20 from 'nuxt_plugin_vueSwiper_5f742e20' // Source: ../../plugins/vueSwiper.js (mode: 'client')
import nuxt_plugin_vueIntro_fd64610c from 'nuxt_plugin_vueIntro_fd64610c' // Source: ../../plugins/vueIntro.js (mode: 'client')
import nuxt_plugin_vTooltip_60b7381a from 'nuxt_plugin_vTooltip_60b7381a' // Source: ../../plugins/vTooltip.js (mode: 'client')
import nuxt_plugin_vueWebStorage_42174bfe from 'nuxt_plugin_vueWebStorage_42174bfe' // Source: ../../plugins/vueWebStorage.js (mode: 'client')
import nuxt_plugin_vueSanitize_16c981c2 from 'nuxt_plugin_vueSanitize_16c981c2' // Source: ../../plugins/vueSanitize.js (mode: 'all')

// Component: <NoSsr>
Vue.component(NoSsr.name, NoSsr)

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>`
Vue.component(Nuxt.name, Nuxt)

// vue-meta configuration
Vue.use(Meta, {
  keyName: 'head', // the component option name that vue-meta looks for meta info on.
  attribute: 'data-n-head', // the attribute name vue-meta adds to the tags it observes
  ssrAttribute: 'data-n-head-ssr', // the attribute name that lets vue-meta know that meta info has already been server-rendered
  tagIDKeyName: 'hid' // the property name that vue-meta uses to determine whether to overwrite or append a tag
})

const defaultTransition = {"name":"page","mode":"out-in","appear":false,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

async function createApp(ssrContext) {
  const router = await createRouter(ssrContext)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Fix SSR caveat https://github.com/nuxt/nuxt.js/issues/3757#issuecomment-414689141
  const registerModule = store.registerModule
  store.registerModule = (path, rawModule, options) => registerModule.call(store, path, rawModule, Object.assign({ preserveState: process.client }, options))

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    router,
    store,
    nuxt: {
      defaultTransition,
      transitions: [ defaultTransition ],
      setTransitions(transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [ transitions ]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },
      err: null,
      dateErr: null,
      error(err) {
        err = err || null
        app.context._errored = !!err
        err = err ? normalizeError(err) : null
        const nuxt = this.nuxt || this.$options.nuxt
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) ssrContext.nuxt.error = err
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    route,
    next,
    error: app.nuxt.error.bind(app),
    store,
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  const inject = function (key, value) {
    if (!key) throw new Error('inject(key, value) has no key provided')
    if (typeof value === 'undefined') throw new Error('inject(key, value) has no value provided')
    key = '$' + key
    // Add into app
    app[key] = value

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) return
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Vue.prototype.hasOwnProperty(key)) {
        Object.defineProperty(Vue.prototype, key, {
          get() {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Plugin execution

  if (process.client && typeof nuxt_plugin_googleanalytics_68dd8e7e === 'function') {
    await nuxt_plugin_googleanalytics_68dd8e7e(app.context, inject)
  }

  if (typeof nuxt_plugin_nuxtdevicedetectplugin4789340e_298842d9 === 'function') {
    await nuxt_plugin_nuxtdevicedetectplugin4789340e_298842d9(app.context, inject)
  }

  if (typeof nuxt_plugin_cookieuniversalnuxt_4d00bf60 === 'function') {
    await nuxt_plugin_cookieuniversalnuxt_4d00bf60(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_37ac50a8 === 'function') {
    await nuxt_plugin_axios_37ac50a8(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_3566aa80 === 'function') {
    await nuxt_plugin_axios_3566aa80(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vueIndicative_328cb63c === 'function') {
    await nuxt_plugin_vueIndicative_328cb63c(app.context, inject)
  }

  if (typeof nuxt_plugin_vueBugsnag_10b846e6 === 'function') {
    await nuxt_plugin_vueBugsnag_10b846e6(app.context, inject)
  }

  if (typeof nuxt_plugin_vueAwesome_41c0d9d8 === 'function') {
    await nuxt_plugin_vueAwesome_41c0d9d8(app.context, inject)
  }

  if (typeof nuxt_plugin_vuetify_0e1f10d7 === 'function') {
    await nuxt_plugin_vuetify_0e1f10d7(app.context, inject)
  }

  if (typeof nuxt_plugin_appyUtil_3ace1371 === 'function') {
    await nuxt_plugin_appyUtil_3ace1371(app.context, inject)
  }

  if (typeof nuxt_plugin_vueMoment_7f2e95a2 === 'function') {
    await nuxt_plugin_vueMoment_7f2e95a2(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vueScrollto_09b8cdc0 === 'function') {
    await nuxt_plugin_vueScrollto_09b8cdc0(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vueClipboard_d0a87ee0 === 'function') {
    await nuxt_plugin_vueClipboard_d0a87ee0(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vueSwiper_5f742e20 === 'function') {
    await nuxt_plugin_vueSwiper_5f742e20(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vueIntro_fd64610c === 'function') {
    await nuxt_plugin_vueIntro_fd64610c(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vTooltip_60b7381a === 'function') {
    await nuxt_plugin_vTooltip_60b7381a(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vueWebStorage_42174bfe === 'function') {
    await nuxt_plugin_vueWebStorage_42174bfe(app.context, inject)
  }

  if (typeof nuxt_plugin_vueSanitize_16c981c2 === 'function') {
    await nuxt_plugin_vueSanitize_16c981c2(app.context, inject)
  }

  // If server-side, wait for async component to be resolved first
  if (process.server && ssrContext && ssrContext.url) {
    await new Promise((resolve, reject) => {
      router.push(ssrContext.url, resolve, () => {
        // navigated to a different route in router guard
        const unregister = router.afterEach(async (to, from, next) => {
          ssrContext.url = to.fullPath
          app.context.route = await getRouteData(to)
          app.context.params = to.params || {}
          app.context.query = to.query || {}
          unregister()
          resolve()
        })
      })
    })
  }

  return {
    app,
    store,
    router
  }
}

export { createApp, NuxtError }
